import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { EFP_COVER_CATEGORIES } from '@om/redux/actions/easiplusFuneralPlan/types';
import {
    getAge,
    getAgeHintText,
    getAgePlaceholderText,
    getCoverAmount,
    getCoverAmountLimits,
    getDisplayCoverAmount,
    getDisplayMonthlyPremium,
    getDisplayParentCoverOptions,
    getParentsDrawerIsOpen,
    getRelationship,
    getShowingAgeValidation,
    getSliderSteps,
} from '@om/redux/selectors/easiplusFuneralPlan/quote';
import {
    setAgeValue,
    setCoverAmountValue,
    setCoverMemberValue,
    setShowingAgeValidation,
} from '@om/redux/actions/easiplusFuneralPlan/quote';
import { OmFormDropdownFieldWrapper, OmFormInputFieldWrapper, OmSlider, OmLoader } from '@om/component-library-react';
import QuoteSideDrawerWrapper from './QuoteSideDrawerWrapper';
import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getIsQuoteReferenceDataValid,
    getIsApplicationReferenceDataValid,
} from '@om/redux/selectors/easiplusFuneralPlan/referenceData';

const FormContent = () => {
    const {
        parentsDrawerIsOpen,
        age,
        coverAmount,
        displayCoverAmount,
        displayMonthlyPremium,
        relationship,
        displayParentsCoverOptions,
        coverAmountLimits,
        ageHintText,
        agePlaceholderText,
        showingAgeValidation,
        sliderSteps,
    } = useSelector(
        createStructuredSelector({
            parentsDrawerIsOpen: getParentsDrawerIsOpen,
            age: getAge,
            coverAmount: getCoverAmount,
            displayCoverAmount: getDisplayCoverAmount,
            displayMonthlyPremium: getDisplayMonthlyPremium,
            relationship: getRelationship,
            displayParentsCoverOptions: getDisplayParentCoverOptions,
            coverAmountLimits: getCoverAmountLimits,
            ageHintText: getAgeHintText,
            agePlaceholderText: getAgePlaceholderText,
            showingAgeValidation: getShowingAgeValidation,
            sliderSteps: getSliderSteps,
        })
    );

    const dispatch = useDispatch();
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={parentsDrawerIsOpen}
                editCategory={EFP_COVER_CATEGORIES.PARENTS_AND_IN_LAWS}
            >
                <h6 className="no-top-margin medium">My Parents & In-laws</h6>
                <OmFormDropdownFieldWrapper
                    align="left"
                    label=" "
                    placeholder="What's your relationship?"
                    placeholderAsLabel={false}
                    formId="addInLaws"
                    size="full"
                    name="Relationship"
                    useNativeMobileDropdown
                    required
                    value={relationship && relationship.value}
                    state={relationship && relationship.status}
                    errorMessage={relationship && relationship.error}
                    options={JSON.stringify(
                        displayParentsCoverOptions &&
                            displayParentsCoverOptions.map((parentOption) => ({
                                value: parentOption.value,
                                label: parentOption.value,
                                disabled: parentOption.covered,
                            }))
                    )}
                    onOnSelect={(event) => dispatch(setCoverMemberValue(event.detail.value))}
                />

                <div>
                    {relationship && relationship.value && (
                        <OmFormInputFieldWrapper
                            id="ageField"
                            mask="number"
                            size="full"
                            align="left"
                            label=" "
                            required
                            keyboardMode="numeric"
                            placeholder={agePlaceholderText}
                            hintText={ageHintText}
                            validationDelay={DEFAULT_VALIDATION_DELAY}
                            value={age && age.value}
                            state={age && age.status}
                            errorMessage={age && age.error}
                            onShowingValidation={(event) => dispatch(setShowingAgeValidation(event.detail))}
                            onPersist={(event) => dispatch(setAgeValue(event.detail))}
                        />
                    )}
                </div>

                <div>
                    {age && age.status === 'valid' && showingAgeValidation && (
                        <>
                            <h6 className="no-bottom-margin">Select cover amount:</h6>
                            <OmSlider
                                stepString={JSON.stringify(sliderSteps)}
                                minLabel={coverAmountLimits.minLabel}
                                maxLabel={coverAmountLimits.maxLabel}
                                value={coverAmount.value}
                                onSliderChange={(event) => dispatch(setCoverAmountValue(event.detail))}
                            />

                            <div className="cover-label-wrapper">
                                <span className="cover-label">
                                    <h5 className="no-bottom-margin no-top-margin medium">{displayCoverAmount}</h5>
                                    <h6 className="small medium no-top-margin">COVER AMOUNT</h6>
                                </span>

                                <span className="premium-label">
                                    <h5 className="no-bottom-margin no-top-margin medium">{displayMonthlyPremium}</h5>
                                    <h6 className="small medium no-top-margin">TOTAL MONTHLY PREMIUM</h6>
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </QuoteSideDrawerWrapper>
        </>
    );
};

const loadingText = (parentsDrawerIsOpen) => {
    return (
        <>
            <QuoteSideDrawerWrapper
                drawerIsOpen={parentsDrawerIsOpen}
                editCategory={EFP_COVER_CATEGORIES.DIRECT_FAMILY}
            >
                <OmLoader loaderText={'Loading...'} isLoading={true} />
            </QuoteSideDrawerWrapper>
        </>
    );
};

const ParentsAndInLawsDrawer = () => {
    const { parentsDrawerIsOpen, quoteReferenceDataValid, applicationReferenceDataValid } = useSelector(
        createStructuredSelector({
            parentsDrawerIsOpen: getParentsDrawerIsOpen,
            quoteReferenceDataValid: getIsQuoteReferenceDataValid,
            applicationReferenceDataValid: getIsApplicationReferenceDataValid,
        })
    );

    if (applicationReferenceDataValid && quoteReferenceDataValid) {
        return <FormContent />;
    }

    return <div>{loadingText(parentsDrawerIsOpen)}</div>;
};

export default ParentsAndInLawsDrawer;
